import { useMemo, useState } from "preact/hooks";
import type { CoreData } from "../data/types";
import { SEX_WORD, clockTime, fmt, genderClass, genderLabel, localDate, longDate, sexIdxFor } from "../data/format";
import { dueDateCalendar } from "../data/calendar";
import { useStore } from "../state/store";
import { ask, decisions, haptic, listByVerdict, native, settings, shareOrCopy, siteOrigin, toast } from "../state/app";
import { choose, chosen, fullName, reminderTime, setReminder, syncReminder, unchoose } from "../state/chosen";
import { closeOverlay, goTab, openOverlay } from "../state/router";
import { count } from "../state/telemetry";
import { Sheet } from "./Sheet";
import { confetti } from "./confetti";
import { IconCheck, IconDownload, IconLink, IconShare } from "./icons";
import { saveFile } from "./saveFile";
import { TAGLINE, shareNameCard } from "./shareImage";

/** Make a name the chosen one (asking first if it replaces another), celebrate, and open its sheet. */
export async function pickName(key: string, name: string): Promise<void> {
  const prev = chosen.get();
  if (prev.key && prev.key !== key) {
    const ok = await ask(`Choose ${name} instead of ${prev.name}?`, { body: `${prev.name} stays on your shortlist.`, ok: `Choose ${name}` });
    if (!ok) return;
  }
  choose(key, name);
  haptic([30, 50, 30, 50, 60]);
  const css = getComputedStyle(document.documentElement);
  const token = (v: string, fallback: string) => css.getPropertyValue(v).trim() || fallback;
  confetti([token("--love", "#b98200"), token("--like", "#2f9a66"), token("--girl", "#d4628c"), token("--boy", "#3f7fcc"), token("--unisex", "#8a6ccc")]);
  openOverlay("chosen");
}

/** On a shortlisted name's page: "This is the one", or a way back to the chosen name's sheet. */
export function ChooseButton({ k, name }: { k: string; name: string }) {
  const isChosen = useStore(chosen, (s) => s.key === k);
  return isChosen ? (
    <button class="choose-btn is-chosen" onClick={() => openOverlay("chosen")}>
      <IconCheck size={18} /> Your chosen name <span class="choose-more">Share the news</span>
    </button>
  ) : (
    <button class="choose-btn" onClick={() => pickName(k, name)}>
      <IconCheck size={18} /> This is the one
    </button>
  );
}

/** At the top of the shortlist once a name is chosen. */
export function ChosenBanner({ core }: { core: CoreData }) {
  const c = useStore(chosen, (s) => s);
  if (!c.key) return null;
  const entry = core.byKey.get(c.key);
  const born = !!localDate(c.bornOn);
  return (
    <section class={`chosen-banner g-${entry ? genderClass(entry) : "unisex"}`} aria-label="Your chosen name">
      <span class="chosen-banner-text">
        <span class="chosen-banner-kicker">Your chosen name</span>
        <span class="chosen-banner-name">{[c.name, c.middle.trim()].filter(Boolean).join(" ")}</span>
      </span>
      <button class="btn btn-small btn-primary" onClick={() => openOverlay("chosen")}>
        {born ? "Announce the birth" : "Share the news"}
      </button>
    </section>
  );
}

const today = (): string => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

/** The chosen name: tell people, set the due date, and announce the birth. */
export function ChosenSheet({ core, open }: { core: CoreData; open: boolean }) {
  const c = useStore(chosen, (s) => s);
  const surname = useStore(settings, (s) => s.surname);
  const map = useStore(decisions, (s) => s.map);
  const [busy, setBusy] = useState(false);
  const entry = c.key ? core.byKey.get(c.key) : undefined;
  const g = entry ? genderClass(entry) : "unisex";
  const full = fullName(c, surname);
  const middles = useMemo(
    () =>
      listByVerdict(map, [2, 1])
        .filter((k) => k !== c.key)
        .map((k) => core.byKey.get(k)?.name)
        .filter((n): n is string => !!n)
        .slice(0, 8),
    [map, c.key],
  );
  const reminderAt = reminderTime(c.due);
  const duePassed = !!reminderAt && reminderAt.getTime() <= Date.now();
  const born = !!localDate(c.bornOn);

  const card = (heading: string, facts: string[]) => ({
    name: c.name,
    gender: g,
    label: entry ? genderLabel(entry) : "Name",
    fullName: full !== c.name ? full : undefined,
    meaning: entry?.meaning ?? undefined,
    facts,
    tagline: TAGLINE[core.country.id],
    heading,
    site: siteOrigin(),
  });
  /** what the records say about the name, for the news picture: "#63 for girls in the UK, 2025" */
  const nameFacts = (): string[] => {
    if (!entry) return [];
    const si = sexIdxFor(entry, "X");
    const place = core.country.id === "other" ? "the UK and US" : `the ${core.country.short}`;
    const out: string[] = [];
    if (entry.r[si]) out.push(`#${fmt(entry.r[si])} for ${SEX_WORD[si]} in ${place}, ${core.latest}`);
    if (entry.c[si]) out.push(`${fmt(entry.c[si])} ${SEX_WORD[si]} given it that year`);
    return out;
  };

  const run = async (job: () => Promise<unknown>) => {
    if (busy) return;
    setBusy(true);
    try {
      await job();
    } catch {
      toast("Couldn't make the picture");
    } finally {
      setBusy(false);
    }
  };

  const shareNews = () =>
    run(async () => {
      const due = localDate(c.due);
      const facts = [
        ...(due && !born ? [`Due ${due.toLocaleDateString(core.country.id === "us" ? "en-US" : "en-GB", { month: "long", year: "numeric" })}`] : []),
        ...nameFacts(),
      ];
      const how = await shareNameCard(card("We've chosen", facts), `We've chosen a name: ${full}`, "share.chosen");
      if (how === "saved") toast("Picture saved");
    });

  const sendLink = async () => {
    count("share.chosen");
    const text = `We've chosen a name: ${full}`;
    if (__PREVIEW__) return shareOrCopy({ title: full, text }, entry?.meaning ? `${text}. It means “${entry.meaning}”.` : text, "Copied, ready to paste");
    // names in the deck have a page on the website; rarer ones open in the app
    const url = entry ? `${siteOrigin()}/names/${encodeURIComponent(c.key)}` : `${siteOrigin()}/#/explore/name/${encodeURIComponent(c.key)}`;
    await shareOrCopy({ title: full, text, url }, `${text} ${url}`, "Copied, ready to paste");
  };

  const announce = () =>
    run(async () => {
      const us = core.country.id === "us";
      const on = longDate(c.bornOn, us);
      const when = `Born ${on}${/^\d{2}:\d{2}$/.test(c.bornAt) ? ` at ${clockTime(c.bornAt, us)}` : ""}`;
      const facts = [when, c.weight.trim()].filter(Boolean);
      const how = await shareNameCard(card("Welcome", facts), `${full}, born ${on}`, "share.birth");
      if (how === "saved") toast("Picture saved");
    });

  const addToCalendar = async () => {
    const ics = dueDateCalendar(c.name, c.due, `${siteOrigin()}/#/list/chosen`);
    if (!ics) return;
    count("remind.calendar");
    await saveFile("due-date.ics", new Blob([ics], { type: "text/calendar" }));
  };

  const setDue = (due: string) => {
    chosen.set({ due });
    void syncReminder();
  };

  const toggleReminder = async (on: boolean) => {
    const now = await setReminder(on);
    if (on && !now) toast("Notifications are off for Nameling. You can turn them on in the iPhone's Settings.", undefined, 6000);
  };

  const chooseAgain = async () => {
    const ok = await ask("Choose a different name?", {
      body: `${c.name} stays on your shortlist. Your due date and birth details are kept.`,
      ok: "Choose again",
    });
    if (!ok) return;
    const was = c.name;
    unchoose();
    closeOverlay();
    goTab("list");
    toast(`${was} is back on your shortlist`);
  };

  if (!c.key)
    return (
      <Sheet open={open} onClose={closeOverlay} title="Your chosen name" size="auto">
        <div class="chosen-empty">
          <p>You haven't chosen a name yet.</p>
          <p class="fhint">When you've decided, open the name from your shortlist and tap This is the one.</p>
          <button
            class="btn btn-primary"
            onClick={() => {
              closeOverlay();
              goTab("list");
            }}
          >
            Go to your shortlist
          </button>
        </div>
      </Sheet>
    );

  return (
    <Sheet open={open} onClose={closeOverlay} title="Your chosen name" class="chosen-sheet">
      <div class={`chosen-hero g-${g}`}>
        <p class="chosen-kicker">
          <IconCheck size={16} /> You've chosen
        </p>
        <p class="chosen-name">{c.name}</p>
        {full !== c.name && <p class="chosen-full">{full}</p>}
        {entry?.meaning && <p class="chosen-meaning">“{entry.meaning}”</p>}
      </div>

      <section class="fsec">
        <h3>Full name</h3>
        <label class="field">
          <span>Middle name (optional)</span>
          <input
            type="text"
            value={c.middle}
            maxLength={40}
            autocomplete="off"
            autocapitalize="words"
            onInput={(ev) => chosen.set({ middle: (ev.target as HTMLInputElement).value })}
          />
        </label>
        {middles.length > 0 && (
          <div class="chips small chosen-middles" role="group" aria-label="Middle names from your shortlist">
            {middles.map((n) => (
              <button
                key={n}
                class={`chip-btn ${c.middle.trim() === n ? "on" : ""}`}
                aria-pressed={c.middle.trim() === n}
                onClick={() => chosen.set({ middle: c.middle.trim() === n ? "" : n })}
              >
                {n}
              </button>
            ))}
          </div>
        )}
        {!surname.trim() && <p class="fhint">Add your surname in Settings to see the full name.</p>}
      </section>

      <section class="fsec">
        <h3>Tell people</h3>
        <p class="fhint">A picture for the family group chat, or a link to {c.name}'s meaning and popularity.</p>
        <div class="action-list">
          <button onClick={shareNews} disabled={busy}>
            <IconShare size={20} /> {__PREVIEW__ ? "Save the news as a picture" : "Share the news as a picture"}
          </button>
          <button onClick={sendLink}>
            <IconLink size={20} /> {__PREVIEW__ ? "Copy the news" : "Send a link"}
          </button>
        </div>
      </section>

      <section class="fsec">
        <h3>Due date</h3>
        <label class="field">
          <span>Date (optional)</span>
          <input type="date" value={c.due} onInput={(ev) => setDue((ev.target as HTMLInputElement).value)} />
        </label>
        {c.due && duePassed && !born && <p class="fhint">That date has passed. Add the birth details below when you're ready.</p>}
        {c.due && !duePassed && native.reminder && (
          <label class="toggle-row">
            <span>
              <span class="toggle-label">Remind me on the day</span>
              <span class="toggle-hint">
                One notification on the morning of {longDate(c.due)}, to make a card announcing the birth whenever you're ready.
              </span>
            </span>
            <input type="checkbox" role="switch" checked={c.remind} onChange={(ev) => toggleReminder((ev.target as HTMLInputElement).checked)} />
            <span class="switch" aria-hidden="true" />
          </label>
        )}
        {c.due && !duePassed && !native.reminder && !__PREVIEW__ && (
          <div class="row-gap chosen-cal">
            <button class="btn btn-small" onClick={addToCalendar}>
              <IconDownload size={16} /> Add it to your calendar
            </button>
          </div>
        )}
      </section>

      <section class="fsec">
        <h3>When the baby's here</h3>
        <p class="fhint">Add the details for a card announcing the birth. They stay on this {__NATIVE__ ? "phone" : "device"}.</p>
        <div class="chosen-birth">
          <label class="field">
            <span>Date of birth</span>
            <input type="date" value={c.bornOn} max={today()} onInput={(ev) => chosen.set({ bornOn: (ev.target as HTMLInputElement).value })} />
          </label>
          <label class="field">
            <span>Time (optional)</span>
            <input type="time" value={c.bornAt} onInput={(ev) => chosen.set({ bornAt: (ev.target as HTMLInputElement).value })} />
          </label>
          <label class="field">
            <span>Weight (optional)</span>
            <input
              type="text"
              value={c.weight}
              maxLength={20}
              placeholder={core.country.id === "us" ? "7 lb 8 oz" : "3.4 kg"}
              onInput={(ev) => chosen.set({ weight: (ev.target as HTMLInputElement).value })}
            />
          </label>
        </div>
        <button class="btn btn-primary chosen-announce" onClick={announce} disabled={!born || busy}>
          <IconShare size={18} /> {__PREVIEW__ ? "Save the announcement" : "Share the announcement"}
        </button>
        {!born && <p class="fhint">Add the date of birth to make the card.</p>}
      </section>

      <section class="fsec">
        <button class="btn btn-small btn-danger-ghost" onClick={chooseAgain}>
          Choose a different name
        </button>
      </section>
    </Sheet>
  );
}
